import { FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { SxProps, Theme } from "@mui/system";
import { useAppDispatch } from "@/store/hooks";
import { useGetUsersMeOrganisationsCurrentDashboardsPossibleWidgetsQuery } from "@/services/cloudchipr.api";
import { setWidgetSelectorState } from "@/store/dashboards/dashboardsSlice";
import { useAppAbility } from "@/services/permissions";

interface DashboardV2AddWidgetActionProps {
  onClose?(): void;
  iconButon?: boolean;
  dashboardId: string;
  isProtected?: boolean;
  iconSx?: SxProps<Theme>;
  menuItemSx?: SxProps<Theme>;
}

export const DashboardV2AddWidgetAction: FC<
  DashboardV2AddWidgetActionProps
> = ({ iconSx, onClose, iconButon, menuItemSx, isProtected, dashboardId }) => {
  const { can } = useAppAbility();
  const canCreateDashboard = can("create", "dashboard");
  const title = iconButon ? "Add Widget" : "";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { refetch } =
    useGetUsersMeOrganisationsCurrentDashboardsPossibleWidgetsQuery({});

  const openWidgetSelector = useCallback(() => {
    navigate(`/dashboards/${dashboardId}`);
    dispatch(setWidgetSelectorState(true));
    refetch();
    onClose?.();
  }, [dispatch, refetch, dashboardId, navigate, onClose]);

  return (
    <Tooltip
      arrow
      title={isProtected ? "Protected dashboards cannot be modified." : title}
      placement={iconButon ? "top" : "right"}
    >
      <span>
        {iconButon ? (
          <IconButton
            size="small"
            sx={iconButtonStyles}
            disabled={isProtected || !canCreateDashboard}
            onClick={openWidgetSelector}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        ) : (
          <MenuItem
            sx={menuItemSx}
            onClick={openWidgetSelector}
            disabled={isProtected || !canCreateDashboard}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" sx={iconSx} />
            </ListItemIcon>
            <Typography variant="body2">Add Widget</Typography>
          </MenuItem>
        )}
      </span>
    </Tooltip>
  );
};

const iconButtonStyles = { p: 0.3, "&:hover": { bgcolor: "grey.200" } };
